import { useState } from 'react';
import type { Deck, User, Slide, Node, UpdateQueue } from '../../types';

interface UseSlideEditorStateProps {
  initialIsEditing: boolean;
}

/**
 * Central state management for SlideEditor component
 *
 * This hook consolidates all useState declarations to provide a single
 * source of truth for component state. Makes state easy to understand
 * and test in isolation.
 */
export function useSlideEditorState({ initialIsEditing }: UseSlideEditorStateProps) {
  // Data state
  const [deck, setDeck] = useState<Deck | null>(null);
  const [user, setUser] = useState<User | null>(null);
  const [currentUser, setCurrentUser] = useState<User | null>(null);
  const [slides, setSlides] = useState<Slide[]>([]);
  const [currentSlideIndex, setCurrentSlideIndex] = useState(0);
  const [currentSlideId, setCurrentSlideId] = useState<number | null>(null);
  const [nodes, setNodes] = useState<Node[]>([]);
  const [slideNodesCache, setSlideNodesCache] = useState<{ [slideId: number]: Node[] }>({});

  // UI state
  const [isEditing, setIsEditing] = useState(initialIsEditing);
  const [loading, setLoading] = useState(true);
  const [showLibrary, setShowLibrary] = useState(false);
  const [showUrlPanel, setShowUrlPanel] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [isAnimating, setIsAnimating] = useState(false);
  const [animatingNodeIds, setAnimatingNodeIds] = useState<Set<string>>(new Set());
  const [activeNodeId, setActiveNodeId] = useState<string | null>(null);

  // Recent items
  const [recentImages, setRecentImages] = useState<string[]>([]);
  const [recentUrls, setRecentUrls] = useState<string[]>([]);

  // Update queue
  const [updateQueue, setUpdateQueue] = useState<UpdateQueue>({});

  return {
    // Data state
    deck,
    setDeck,
    user,
    setUser,
    currentUser,
    setCurrentUser,
    slides,
    setSlides,
    currentSlideIndex,
    setCurrentSlideIndex,
    currentSlideId,
    setCurrentSlideId,
    nodes,
    setNodes,
    slideNodesCache,
    setSlideNodesCache,

    // UI state
    isEditing,
    setIsEditing,
    loading,
    setLoading,
    showLibrary,
    setShowLibrary,
    showUrlPanel,
    setShowUrlPanel,
    showSettings,
    setShowSettings,
    showDeleteConfirm,
    setShowDeleteConfirm,
    isAnimating,
    setIsAnimating,
    animatingNodeIds,
    setAnimatingNodeIds,
    activeNodeId,
    setActiveNodeId,

    // Recent items
    recentImages,
    setRecentImages,
    recentUrls,
    setRecentUrls,

    // Update queue
    updateQueue,
    setUpdateQueue,
  };
}
